/**
 * Online Status Manager for Web App
 *
 * Backend-Only Firebase Architecture:
 * All presence writes go through the backend API /api/v2/user/presence
 * The backend is the single source of truth for /status/{userId} in Firebase
 *
 * Client apps are READ-ONLY for the status node
 */

class OnlineStatusManager {
  constructor() {
    this.currentUserId = null;
    this.accessToken = null;
    this.heartbeatInterval = null;
    this.isOnline = false;

    // MULTI-DEVICE: Stable device ID stored in localStorage
    this.deviceId = this.getOrCreateDeviceId();

    const buildDate = new Date();
    const buildNumber = `${String(buildDate.getMonth() + 1).padStart(
      2,
      "0"
    )}${String(buildDate.getDate()).padStart(2, "0")}`;
    this.appVersion = process.env.REACT_APP_VERSION || `1.0.0(${buildNumber})`;
    this.browserInfo = this.getBrowserInfo();
    this.deviceInfo = this.getDeviceInfo();
    this.platform = "web";
    this.appType = "web_react";

    // API base URL - strip /api/v1 or /api/v2 suffix if present in env var.
    // react-app#386: the fallback must match baseApi's (staging) — a build
    // with a missing env var must never quietly send presence traffic to
    // production.
    let baseUrl =
      process.env.REACT_APP_BASE_URL || "https://stg2-api.compasscare.app";
    if (baseUrl.endsWith("/api/v1")) baseUrl = baseUrl.replace("/api/v1", "");
    if (baseUrl.endsWith("/api/v2")) baseUrl = baseUrl.replace("/api/v2", "");
    this.baseUrl = baseUrl;

    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
  }

  /**
   * Set user online after login
   * @param {string|number} userId - User ID
   * @param {string} accessToken - JWT access token for API calls
   */
  setUserOnline(userId, accessToken) {
    if (!userId) {
      console.warn("[PRESENCE] No userId provided to setUserOnline");
      return;
    }

    if (!accessToken) {
      console.warn("[PRESENCE] No accessToken provided to setUserOnline");
      return;
    }

    this.currentUserId = String(userId);
    this.accessToken = accessToken;

    console.log("👤 [PRESENCE] Updating user presence via backend API:", {
      userId: this.currentUserId,
    });

    this.updateOnlineStatus(true, "login");
    this.setupEventListeners();
    this.startHeartbeat();
  }

  /**
   * Set user offline (logout or manual)
   */
  async setUserOffline() {
    if (!this.currentUserId) {
      return;
    }

    // Keyed shape, not a standalone argument (#528).
    console.log(
      "👤 [PRESENCE] Updating user presence via backend API:",
      { userId: this.currentUserId }
    );
    await this.updateOnlineStatus(false, "logout");
    this.cleanup();
  }

  /**
   * Update online status via backend API
   * @param {boolean} isOnline
   * @param {string} action - login, logout, heartbeat, app_background, app_foreground
   */
  async updateOnlineStatus(isOnline, action = "heartbeat") {
    if (!this.currentUserId || !this.accessToken) {
      console.warn("[PRESENCE] Cannot update status - not configured");
      return;
    }

    // Get comprehensive metadata for debugging
    const metadata = this.getComprehensiveMetadata();

    const body = {
      userId: parseInt(this.currentUserId, 10),
      isOnline,
      action,
      // MULTI-DEVICE: Stable device identifier (prevents 100s of device entries)
      deviceId: this.deviceId,
      // Basic metadata
      appType: this.appType,
      platform: this.platform,
      appVersion: this.appVersion,
      deviceInfo: this.deviceInfo,
      browserInfo: this.browserInfo,
      // Comprehensive metadata
      ...metadata,
    };

    try {
      const response = await fetch(`${this.baseUrl}/api/v2/user/presence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
          "X-App-Platform": this.platform,
          "X-App-Version": this.appVersion,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        this.isOnline = isOnline;
        console.log(
          `✅ [PRESENCE] Status updated: isOnline=${isOnline}, action=${action}`
        );
      } else {
        const error = await response.text();
        // The action is named on the FAILURE path too. A failing heartbeat
        // logged without it is unrecognisable to the breadcrumb filter, so it
        // survived every 30s and recreated the presence cadence the drop exists
        // to prevent — the success path was protected, its twin was not.
        console.error(
          `❌ [PRESENCE] Failed to update status: ${response.status}, action=${action}`,
          error
        );
      }
    } catch (error) {
      // Same reason as above: a rejected request is still a heartbeat.
      console.error(
        `❌ [PRESENCE] Failed to update status, action=${action}:`,
        error
      );
    }
  }

  /**
   * Setup browser event listeners
   */
  setupEventListeners() {
    // Tab visibility changes
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Window/tab close
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    // Network status
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }

  /**
   * Handle tab visibility change
   * Note: User stays ONLINE even when tab is hidden - they're still logged in
   * Only log out when tab/window is actually closed (beforeunload)
   *
   * #528: both branches log the IDENTICAL line. They used to say "Tab hidden -
   * user stays ONLINE" and "Tab visible - refreshing online status", and the
   * breadcrumb filter could not save them: it removes direction WORDS, and here
   * the datum is the DISTINCTION between the two messages, not any word in
   * either. Two different strings is itself the disclosure, so the fix is at
   * source. Keep them identical — a helpful-looking edit to one of them
   * reopens this.
   */
  handleVisibilityChange() {
    if (document.hidden) {
      console.log("👀 [PRESENCE] Visibility changed - re-evaluating presence");
      // Don't set offline - user is still logged in, just not focused
      // Only reduce heartbeat frequency to save resources
      this.stopHeartbeat();
      // Start slower heartbeat (every 2 minutes) while in background
      this.heartbeatInterval = setInterval(() => {
        if (this.isOnline) {
          this.updateOnlineStatus(true, "background_heartbeat");
        }
      }, 120000); // 2 minutes
    } else {
      console.log("👀 [PRESENCE] Visibility changed - re-evaluating presence");
      this.updateOnlineStatus(true, "app_foreground");
      this.startHeartbeat(); // Back to normal 30s heartbeat
    }
  }

  /**
   * Handle window/tab close
   * GMA-1440: uses fetch({ keepalive: true }) so the request survives page
   * unload while still sending the Authorization header. (navigator.sendBeacon
   * could not set headers, so it leaked the token into the URL and the backend
   * 401'd it.) Best-effort — the stopped 30s heartbeat + server-side presence
   * TTL are the backstop if the unload request does not complete.
   */
  handleBeforeUnload() {
    console.log("🚪 [PRESENCE] Window closing - updating presence");

    // GMA-1440: use fetch({ keepalive: true }) instead of navigator.sendBeacon.
    // A keepalive request survives page unload like a beacon, but ALSO supports
    // request headers — so the auth token rides in the Authorization header the
    // backend actually reads, instead of the URL query string. sendBeacon could
    // not set headers, so it put `?token=` in the URL, which the backend
    // rejected with 401 (flooding the logs and never marking the user offline).
    // Cross-origin + Authorization means this preflights, so delivery is
    // best-effort on unload — acceptable here since the stopped heartbeat +
    // server-side presence TTL also mark the user offline, and the prior
    // sendBeacon path never succeeded (always 401) anyway.
    if (this.accessToken && this.currentUserId) {
      const body = JSON.stringify({
        userId: parseInt(this.currentUserId, 10),
        isOnline: false,
        // MULTI-DEVICE: Include stable device identifier
        deviceId: this.deviceId,
        appType: this.appType,
        platform: this.platform,
        appVersion: this.appVersion,
        deviceInfo: this.deviceInfo,
        action: "logout",
      });

      fetch(`${this.baseUrl}/api/v2/user/presence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
          "X-App-Platform": this.platform,
          "X-App-Version": this.appVersion,
        },
        body,
        keepalive: true,
      }).catch(() => {
        // Best-effort during unload; nothing to recover if it fails.
      });
    }
  }

  /**
   * Handle browser going online
   */
  handleOnline() {
    console.log("🌐 [PRESENCE] Browser connectivity changed - re-evaluating presence");
    if (!document.hidden) {
      this.updateOnlineStatus(true, "app_foreground");
      this.startHeartbeat();
    }
  }

  /**
   * Handle browser going offline
   */
  handleOffline() {
    console.log("🌐 [PRESENCE] Browser connectivity changed - re-evaluating presence");
    this.stopHeartbeat();
  }

  /**
   * Start heartbeat to keep presence fresh
   * Updates every 30 seconds via backend API
   */
  startHeartbeat() {
    // Clear any existing interval
    this.stopHeartbeat();

    // Update every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.isOnline && !document.hidden) {
        this.updateOnlineStatus(true, "heartbeat");
      }
    }, 30000); // 30 seconds

  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Cleanup on logout or unmount
   */
  cleanup() {
    this.stopHeartbeat();
    this.removeEventListeners();
    this.currentUserId = null;
    this.accessToken = null;
    this.isOnline = false;
  }

  /**
   * Get browser information
   * @returns {string}
   */
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browser = "Unknown";
    let version = "";

    if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
      browser = "Chrome";
      version = userAgent.match(/Chrome\/(\d+)/)?.[1] || "";
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      browser = "Safari";
      version = userAgent.match(/Version\/(\d+)/)?.[1] || "";
    } else if (userAgent.includes("Firefox")) {
      browser = "Firefox";
      version = userAgent.match(/Firefox\/(\d+)/)?.[1] || "";
    } else if (userAgent.includes("Edg")) {
      browser = "Edge";
      version = userAgent.match(/Edg\/(\d+)/)?.[1] || "";
    }

    return `${browser} ${version}`.trim();
  }

  /**
   * Get device/platform information
   * @returns {string}
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let device = "Desktop";
    let os = "Unknown";

    // Detect OS
    if (userAgent.includes("Win")) {
      os = "Windows";
    } else if (userAgent.includes("Mac")) {
      os = "macOS";
    } else if (userAgent.includes("Linux")) {
      os = "Linux";
    } else if (userAgent.includes("Android")) {
      os = "Android";
      device = "Mobile";
    } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
      os = "iOS";
      device = userAgent.includes("iPad") ? "Tablet" : "Mobile";
    }

    return `${device} - ${os}`;
  }

  /**
   * Get comprehensive metadata for debugging calling issues
   * @returns {object}
   */
  getComprehensiveMetadata() {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    return {
      // OS and device
      osVersion:
        navigator.userAgentData?.platform || navigator.platform || "unknown",
      deviceModel: this.getDeviceModel(),
      sdkVersion: `React ${this.getReactVersion()}`,
      buildNumber: process.env.REACT_APP_BUILD_NUMBER || "unknown",

      // Network
      networkType: connection?.effectiveType || "unknown",

      // Localization
      locale: navigator.language || "unknown",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",

      // Screen
      screenResolution: `${screen.width}x${screen.height}`,

      // Battery (not reliably available in web)
      batteryLevel: null,
      isLowPowerMode: false,
    };
  }

  /**
   * Get device model/type
   * @returns {string}
   */
  getDeviceModel() {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";
    if (/Android/.test(ua)) {
      // Try to extract device model from Android UA
      const match = ua.match(/Android.*;\s*([^)]+)\s*\)/);
      return match ? match[1].trim() : "Android";
    }
    if (/Macintosh/.test(ua)) return "Mac";
    if (/Windows/.test(ua)) return "Windows PC";
    if (/Linux/.test(ua)) return "Linux PC";
    return "Desktop";
  }

  /**
   * Get React version
   * @returns {string}
   */
  getReactVersion() {
    // Try to get React version from package
    return "18.x"; // React 18 is commonly used
  }

  /**
   * MULTI-DEVICE: Get or create a stable device ID
   * Stored in localStorage so it persists across sessions
   * @returns {string}
   */
  getOrCreateDeviceId() {
    const STORAGE_KEY = "compasscare_device_id";

    try {
      let deviceId = localStorage.getItem(STORAGE_KEY);

      if (!deviceId) {
        // Generate a new stable device ID
        // Format: web_{browser}_{random UUID}
        const browser = this.getBrowserInfo().split(" ")[0] || "unknown";
        const uuid = crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        deviceId = `web_${browser.toLowerCase()}_${uuid}`;

        localStorage.setItem(STORAGE_KEY, deviceId);
      }

      return deviceId;
    } catch (e) {
      // Fallback if localStorage fails (private browsing, etc.)
      console.warn(
        "⚠️ [PRESENCE] localStorage unavailable, using session deviceId"
      );
      return `web_session_${Date.now()}`;
    }
  }

  /**
   * Force refresh online status
   */
  refreshOnlineStatus() {
    if (this.currentUserId && !document.hidden) {
      console.log("🔄 [PRESENCE] Force refreshing presence status");
      this.updateOnlineStatus(true, "heartbeat");
    }
  }
}

// Singleton instance
const onlineStatusManager = new OnlineStatusManager();

export default onlineStatusManager;
